export default () => {
  const MOBILE_BREAKPOINT = 1024

  const MEGA_MENU_UNROLL_MS = 350
  const MEGA_MENU_FADE_MS = 200
  const MEGA_MENU_CONTENT_DELAY_MS = Math.round(MEGA_MENU_UNROLL_MS * 0.4)

  return {
    isMobile: window.innerWidth < MOBILE_BREAKPOINT,
    isLanguagesOpen: false,
    isMobileMenuOpen: false,
    isDesktopMenuOpen: false,
    isReduced: false,
    currentDesktopMenuIndex: 0,
    visibleDesktopMenuIndex: 0,
    isDesktopMenuContentVisible: false,
    desktopMenuHeight: '0px',
    desktopMenuTimer: 0,

    init() {
      this.$store.layout.mobileMenuOpen = false

      // BFCache / iOS restore only: reset so PWA bottom nav is not stuck hidden.
      // `pageshow` also fires on every regular load (`event.persisted` is false then),
      // so without this check a slow-loading page could fire it after the user has
      // already opened the mobile menu, force-closing it out from under them.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return
        this.$store.layout.mobileMenuOpen = false
        this.isMobileMenuOpen = false
      })

      window.addEventListener('resize', () => {
        this.isMobile = window.innerWidth < MOBILE_BREAKPOINT

        if (this.isDesktopMenuOpen && !this.isMobile) {
          this.desktopMenuHeight = this.measureMegaMenuPanel(this.visibleDesktopMenuIndex)
        }
      })

      this.initReducedOnScroll()
      this.initMobileViewportHeight()

      const syncBodyOverflow = () => {
        document.body.classList.toggle('overflow-hidden', this.isMobileMenuOpen || this.isDesktopMenuOpen)
      }

      this.$watch('isDesktopMenuOpen', syncBodyOverflow)
      this.$watch('isMobileMenuOpen', (value: boolean) => {
        syncBodyOverflow()
        this.$store.layout.mobileMenuOpen = value
      })

      this.$watch('isMobile', (value: boolean) => {
        if (!value) this.isMobileMenuOpen = false
        if (value) this.closeMegaMenu()
      })
    },

    initReducedOnScroll() {
      const sentinel = document.createElement('div')

      document.body.prepend(sentinel)
      document.body.classList.add('no-transition')

      let observer: IntersectionObserver

      const observeSentinel = () => {
        observer?.disconnect()

        const headerHeight = this.$el.offsetHeight * 0.75

        this.isReduced = window.scrollY > 0 && window.scrollY >= headerHeight

        observer = new IntersectionObserver(([entry]) => {
          // scrollY === 0 is always the non-reduced state regardless of what
          // the IO reports (can misfire on viewport resize or in test iframes).
          this.isReduced = window.scrollY > 0 && !entry.isIntersecting
        }, {
          rootMargin: `${headerHeight}px 0px 0px 0px`,
        })
        observer.observe(sentinel)
      }

      observeSentinel()

      requestAnimationFrame(() => document.body.classList.remove('no-transition'))

      // The header's real height (e.g. logo images) may not be known yet at
      // init time, so re-measure once everything has finished loading.
      window.addEventListener('load', observeSentinel)
    },

    syncViewportHeight() {
      const height = window.visualViewport?.height ?? window.innerHeight

      document.documentElement.style.setProperty('--mobile-vh', `${ height }px`)
    },

    initMobileViewportHeight() {
      this.syncViewportHeight()

      window.addEventListener('resize', () => this.syncViewportHeight())
      window.visualViewport?.addEventListener('resize', () => this.syncViewportHeight())
    },

    toggleMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
      if (this.isMobileMenuOpen) this.syncViewportHeight()
    },

    toggleLanguages() {
      this.isLanguagesOpen = !this.isLanguagesOpen
    },

    toggleMegaMenu(index: number) {
      if (this.isDesktopMenuOpen && this.currentDesktopMenuIndex === index) {
        this.closeMegaMenu()
        return
      }

      if (this.isDesktopMenuOpen) {
        this.switchMegaMenu(index)
        return
      }

      this.openMegaMenu(index)
    },

    openMegaMenu(index: number) {
      this.currentDesktopMenuIndex = index
      this.visibleDesktopMenuIndex = index
      this.isDesktopMenuOpen = true
      this.desktopMenuHeight = this.measureMegaMenuPanel(index)

      this.deferMegaMenu(() => {
        this.isDesktopMenuContentVisible = true
      }, MEGA_MENU_CONTENT_DELAY_MS)
    },

    switchMegaMenu(index: number) {
      this.currentDesktopMenuIndex = index
      this.isDesktopMenuContentVisible = false

      this.desktopMenuHeight = this.measureMegaMenuPanel(index)

      this.deferMegaMenu(() => {
        this.visibleDesktopMenuIndex = index
        this.isDesktopMenuContentVisible = true
      }, MEGA_MENU_FADE_MS)
    },

    closeMegaMenu() {
      if (!this.isDesktopMenuOpen) return

      this.clearMegaMenuTimer()

      this.isDesktopMenuOpen = false
      this.isDesktopMenuContentVisible = false
      this.desktopMenuHeight = '0px'
    },

    measureMegaMenuPanel(index: number): string {
      const panel = this.$root.querySelector<HTMLElement>(`[data-mega-menu-panel="${ index }"]`)

      return `${ panel?.offsetHeight ?? 0 }px`
    },

    deferMegaMenu(callback: () => void, delay: number) {
      this.clearMegaMenuTimer()
      this.desktopMenuTimer = window.setTimeout(callback, delay)
    },

    clearMegaMenuTimer() {
      window.clearTimeout(this.desktopMenuTimer)
    },
  }
}
